import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { makeStyles, Stack, Text, useTheme } from '@fluentui/react';
import { formatNumberToDisplay, formatNumberToDisplayPercent, getCleanValueDiff, roundToNearestValueStep } from '../../mobileUtils';
import { useMobile } from '../../useMobile';
import { useMobileContext } from '../../MobileContext';
import { useGesture } from '@use-gesture/react';
import { convertHoursToProcent, convertProcentToHours, getCapacityPrDay } from '../../../components/WeekOverview/Cell.helpers';
import { MathToFixed } from '../../../components/Utils';
import { periodFakeDay } from '../../mobileConstants';
import { useConfigValuesState } from '../../../hooks/useConfigValue';
import { UserRegistrationPeriod } from '../../../api/generated/data-contracts';
import { useTranslation } from 'react-i18next';
import { format } from '../../../helpers/date';

interface IProps {
    hours: number;
    editTask: UserRegistrationPeriod;
    selectedDate: Date;
    isReadOnly: boolean;
}

// const pixelsDraggedToTrigger = 8;
const maxHoursInSlider = 12;
const maxHoursInInput = 24;
const pixelsDraggedToTriggerChange = 6;

export const TimeSlider: React.FC<IProps> = props => {
    const { t } = useTranslation('timesheet');
    const theme = useTheme();
    const mobCtx = useMobileContext();
    const [state, setState] = useState<number>();
    const [isDragging, setIsDragging] = useState(false);
    const [isEditmode, setIsEditmode] = useState(false);
    // fix for double save when using input
    const [lastUsed, setLastUsed] = useState<'input' | 'drag' | undefined>();
    const classes = getStyles();
    const textfieldRef = useRef<any>();
    // Track whether a blur-save is in progress to prevent gesture interference
    const isSavingFromBlurRef = useRef(false);
    // const useGesture = createUseGesture([dragAction]);
    const {
        initialData: {
            configuration: { timeEntryRoundingInterval, capacityDefault, lockProcessedTimeEntries },
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const userCapacity = useMemo(() => user.availability ?? user.calendar?.dailyCapacity ?? capacityDefault, [user, capacityDefault]);

    const enabledDays = useMemo(() => mobCtx.weekConfig.filter(day => day.enabled).map(day => day.name), [mobCtx.weekConfig]);

    const maxValues = useMemo(() => {
        if (!mobCtx.isPeriodView) {
            return {
                slider: maxHoursInSlider,
                input: maxHoursInInput,
            };
        }
        const periodMaxHours = Math.round(mobCtx.workingPeriodDays.length * getCapacityPrDay(mobCtx.workingPeriodDays, userCapacity));
        if (mobCtx.showAsPercent) {
            return {
                slider: periodMaxHours,
                input: 100,
            };
        }
        return {
            slider: periodMaxHours,
            input: periodMaxHours,
        };
    }, [mobCtx.isPeriodView, mobCtx.showAsPercent, mobCtx.workingPeriodDays, userCapacity]);

    const onTrigger = (direction: 'left' | 'right') => {
        let amount = 0.25;
        if (mobCtx.isPeriodView) {
            amount = mobCtx.showAsPercent || timeEntryRoundingInterval === 0 ? 1 : 0.5;
        } else if (timeEntryRoundingInterval === 0) {
            amount = 1;
        } else if (timeEntryRoundingInterval === 0.5) {
            amount = 0.5;
        } else if (timeEntryRoundingInterval === 0.1) {
            amount = 0.2;
        }

        if (direction === 'left') {
            setState(s => {
                if (mobCtx.showAsPercent) {
                    const percent = convertHoursToProcent(Number(s), mobCtx.workingPeriodDays.length, getCapacityPrDay(mobCtx.workingPeriodDays, userCapacity));
                    const newPercent = roundToNearestValueStep(percent - amount, timeEntryRoundingInterval);
                    const newValue = convertProcentToHours(
                        newPercent,
                        mobCtx.workingPeriodDays.length,
                        getCapacityPrDay(mobCtx.workingPeriodDays, userCapacity),
                    );
                    return newValue < 0 ? 0 : newValue;
                } else {
                    const newValue = roundToNearestValueStep(s - amount, timeEntryRoundingInterval);
                    return newValue < 0 ? 0 : newValue;
                }
            });
        } else if (direction === 'right') {
            setState(s => {
                if (mobCtx.showAsPercent) {
                    const percent = convertHoursToProcent(Number(s), mobCtx.workingPeriodDays.length, getCapacityPrDay(mobCtx.workingPeriodDays, userCapacity));
                    const newPercent = roundToNearestValueStep(percent + amount, timeEntryRoundingInterval);
                    const newValue = convertProcentToHours(
                        newPercent,
                        mobCtx.workingPeriodDays.length,
                        getCapacityPrDay(mobCtx.workingPeriodDays, userCapacity),
                    );
                    return newValue > maxValues.slider ? maxValues.slider : newValue;
                } else {
                    const newValue = roundToNearestValueStep(s + amount, timeEntryRoundingInterval);
                    return newValue > maxValues.slider ? maxValues.slider : newValue;
                }
            });
        }
    };

    const readonlyReason = useMemo(() => {
        if (props.editTask.timeEntries[0]?.approval?.externalId && lockProcessedTimeEntries) {
            return t('readOnlyMessages.externallyLocked');
        }
    }, [lockProcessedTimeEntries, props.editTask.timeEntries, t]);

    const readonly = Boolean(readonlyReason);

    const bind = useGesture(
        {
            onDrag: ({ memo, last, delta: [dx], active, tap }) => {
                if (readonly) {
                    return;
                }
                // Ignore gesture events that fire as a side-effect of the
                // input blur (the same tap that dismisses the keyboard can
                // hit the newly-mounted drag area).
                if (isSavingFromBlurRef.current) {
                    return memo;
                }
                if (tap && !active) {
                    return memo;
                }
                if (last) {
                    setIsDragging(false);
                } else if (!isDragging) {
                    setIsDragging(true);
                    setLastUsed('drag');
                }

                if (!memo) {
                    memo = {
                        direction: dx > 0 ? 'right' : 'left',
                        count: 0,
                    };
                } else {
                    const currentDirection = dx > 0 ? 'right' : 'left';
                    const currentMove = Math.abs(dx);
                    if (currentDirection === memo.direction) {
                        const currentCount = memo.count + currentMove;

                        if (currentCount >= pixelsDraggedToTriggerChange) {
                            onTrigger(currentDirection);
                            memo.count = currentCount - pixelsDraggedToTriggerChange;
                        } else {
                            memo.count = currentCount;
                        }
                    } else {
                        memo.direction = currentDirection;
                        memo.count = 0;
                    }
                }

                return memo;
            },
            // the empty onClick is needed because of some weird bug
            // the onClick 'area' on fluent's TextField is way bigger than the actual element
            // i have no idea why, but this empty onClick fixes it
            onClick: () => {},
        },
        {
            drag: {
                // filterTaps: true,
                preventScroll: true,
                axis: 'x',
            },
        },
    );

    useEffect(() => {
        if (!textfieldRef.current) {
            return;
        }
        textfieldRef.current.focus();
    }, [isEditmode]);

    useEffect(() => {
        setState(props.hours);
    }, [props.hours]);

    const values = useMemo(() => {
        if (mobCtx.showAsPercent) {
            return {
                hours: 0,
                minutes: 0,
                percent: `${formatNumberToDisplayPercent(state, mobCtx.workingPeriodDays, userCapacity, timeEntryRoundingInterval)}`,
            };
        }

        const formatted = formatNumberToDisplay(state, timeEntryRoundingInterval);
        const newHour = Math.round(Math.trunc(formatted));
        const newMinute = Math.round((formatted - newHour) * 0.6 * 100);

        return {
            hours: newHour,
            minutes: newMinute,
            percent: '',
        };
    }, [state, mobCtx.showAsPercent]);

    const onSavePeriod = useCallback(
        (newHours: number) => {
            useMobile.getState().setIsloading({ active: true, overlay: false });
            const timeEntry = props.editTask.timeEntries?.[0];

            // Optimistically update the store so the UI reflects the new
            // value immediately, without waiting for the API round-trip.
            // This mirrors what onSaveNormal does for the day view.
            const dateFormat = useMobile.getState().dateFormat;
            const workingDaysCount = mobCtx.selectedPeriodDays.filter(day => enabledDays.some(ed => ed.toLowerCase() === day.dayName.toLowerCase())).length;
            const hoursPrDay = MathToFixed(newHours / (workingDaysCount > 0 ? workingDaysCount : mobCtx.selectedPeriodDays.length), 2);
            const oldEntries = [...(props.editTask.timeEntries || [])];
            const optimisticEntries = [...oldEntries];
            mobCtx.selectedPeriodDays.forEach(day => {
                const isWorkingDay = enabledDays.some(ed => ed.toLowerCase() === day.dayName.toLowerCase());
                const dayHours = isWorkingDay ? hoursPrDay : 0;
                const indexOfEntry = optimisticEntries.findIndex(
                    entry => format(new Date(entry.date), dateFormat) === format(new Date(day.dayDate), dateFormat),
                );
                if (indexOfEntry !== -1) {
                    optimisticEntries[indexOfEntry] = {
                        ...optimisticEntries[indexOfEntry],
                        hours: dayHours,
                        comment: timeEntry?.comment ?? '',
                        flagged: timeEntry?.flagged ?? false,
                    };
                } else if (isWorkingDay) {
                    optimisticEntries.push({
                        id: undefined,
                        assignment: { workType: props.editTask.workType } as any,
                        date: day.dayDate.toString(),
                        hours: dayHours,
                        comment: timeEntry?.comment ?? '',
                        flagged: timeEntry?.flagged ?? false,
                        userReportingPeriod: mobCtx.selectedTimePeriod,
                    });
                }
            });
            useMobile.getState().updateTask({
                newTask: { ...props.editTask, timeEntries: optimisticEntries },
                updateEditTask: true,
            });

            useMobile
                .getState()
                .updateCellPeriod({
                    taskId: props.editTask.taskId,
                    totalHours: newHours,
                    periodDays: mobCtx.selectedPeriodDays,
                    enabledDays,
                    comment: timeEntry?.comment ?? '',
                    flagged: timeEntry?.flagged ?? false,
                    selectedTimePeriod: mobCtx.selectedTimePeriod,
                    workType: props.editTask.workType,
                    updateTask: true,
                })
                .then(() => {
                    mobCtx.onUpdateSuccessHighlightRow(props.editTask.taskId, periodFakeDay, null, props.editTask.workType?.id);
                })
                .catch(err => {
                    console.error('[TimeSlider] onSavePeriod error:', err);
                })
                .finally(() => {
                    useMobile.getState().setIsloading({ active: false, overlay: false });
                });
        },
        [props.editTask.timeEntries, props.selectedDate, props.hours, state, props.editTask, mobCtx.selectedTimePeriod, mobCtx.selectedPeriodDays, enabledDays],
    );

    const onSaveNormal = useCallback(
        (newHours: number) => {
            useMobile.getState().setIsloading({ active: true, overlay: false });
            const timeEntry = props.editTask.timeEntries?.[0];

            if (timeEntry) {
                useMobile.getState().updateTask({
                    newTask: { ...props.editTask, timeEntries: [{ ...timeEntry, hours: newHours }] },
                    updateEditTask: true,
                });
            }

            useMobile.getState().updateCell({
                taskId: props.editTask.taskId,
                date: props.selectedDate,
                hours: newHours,
                comment: timeEntry?.comment ?? '',
                flagged: timeEntry?.flagged ?? false,
                timePeriodId: mobCtx.selectedTimePeriod.reportPeriodId,
                workType: props.editTask.workType,
                // uiCtx: uiCtx,
                onSuccess: newTimeEntry => {
                    console.debug('success', newTimeEntry);
                    useMobile.getState().setIsloading({ active: false, overlay: false });
                    mobCtx.onUpdateSuccessHighlightRow(props.editTask.taskId, null, newTimeEntry.date, props.editTask.workType?.id);
                },
                updateTask: timeEntry ? false : true,
            });
        },
        [props.editTask.timeEntries, props.selectedDate, props.hours, state],
    );

    const onSave = useCallback(
        (newHours: number) => {
            if (mobCtx.isPeriodView) {
                onSavePeriod(newHours);
            } else {
                onSaveNormal(newHours);
            }
        },
        [onSaveNormal, onSavePeriod, mobCtx.isPeriodView],
    );

    useEffect(() => {
        if (isDragging || state === undefined || lastUsed === undefined || lastUsed === 'input') {
            return;
        }

        const valueDiff = getCleanValueDiff(state, props.hours);

        if (valueDiff !== 0) {
            onSave(state);
        }
    }, [isDragging]);

    const onBlurEditmode = (newValue: number) => {
        const valueDiff = getCleanValueDiff(newValue, props.hours);

        setLastUsed('input');
        // Mark that a blur-triggered save is in progress so the drag
        // gesture handler ignores the tap event that caused this blur.
        isSavingFromBlurRef.current = true;

        if (valueDiff !== 0) {
            onSave(newValue);
        } else {
            // Value didn't change — reset local state to props.hours so
            // the display stays in sync (fixes "0" lingering in the field
            // when the stored value is already 0).
            setState(props.hours);
        }

        if (isDragging) {
            setIsDragging(false);
        }

        // Clear the blur-save guard after the current event cycle so
        // subsequent genuine drag gestures are not blocked.
        requestAnimationFrame(() => {
            isSavingFromBlurRef.current = false;
        });
    };

    const sliderProcessWidth = useMemo(() => {
        return (state / maxValues.slider) * 100 >= 100 ? 100 : (state / maxValues.slider) * 100;
    }, [state, maxValues.slider]);

    const getDefaultInputValue = () => {
        if (mobCtx.showAsPercent) {
            return `${formatNumberToDisplayPercent(state, mobCtx.workingPeriodDays, userCapacity, timeEntryRoundingInterval)}`;
        }
        return roundToNearestValueStep(state, timeEntryRoundingInterval);
    };

    if (state === undefined) {
        return null;
    }

    return (
        <div className={classes.root}>
            {isEditmode ? (
                <div className={classes.numberInputContainer}>
                    <input
                        ref={textfieldRef}
                        type="number"
                        min={0}
                        max={maxValues.input}
                        autoFocus
                        autoComplete="off"
                        className={classes.numberInput}
                        defaultValue={`${getDefaultInputValue()}`}
                        onBlur={(e: any) => {
                            let newValue = Number(e.target.value);
                            if (Number.isNaN(newValue)) {
                                setIsEditmode(false);
                                return;
                            }

                            newValue = roundToNearestValueStep(newValue, timeEntryRoundingInterval);

                            if (newValue < 0) {
                                newValue = 0;
                            } else if (newValue > maxValues.input) {
                                newValue = maxValues.input;
                            }
                            if (mobCtx.showAsPercent) {
                                newValue = convertProcentToHours(
                                    newValue,
                                    mobCtx.workingPeriodDays.length,
                                    getCapacityPrDay(mobCtx.workingPeriodDays, userCapacity),
                                );
                            }
                            setState(newValue);
                            onBlurEditmode(newValue);
                            // Defer switching out of edit mode so the save
                            // initiated by onBlurEditmode is not disrupted
                            // by the gesture handler on the newly-mounted
                            // drag area receiving the same tap event.
                            requestAnimationFrame(() => {
                                setIsEditmode(false);
                            });
                        }}
                    />
                </div>
            ) : (
                <Stack
                    horizontal
                    styles={{ root: classes.textContainer }}
                    onClick={() => {
                        if (!readonly) {
                            setIsEditmode(true);
                        }
                    }}
                >
                    {readonly ? (
                        <Text styles={{ root: { textAlign: 'center', width: '100%' } }}>
                            <b>{readonlyReason}</b>
                        </Text>
                    ) : mobCtx.showAsPercent ? (
                        <Text styles={{ root: classes.hourMinute }} style={{ width: 70, left: 'calc(50% - 50px)' }}>
                            <b className={classes.percent}>{values.percent}</b> %
                        </Text>
                    ) : (
                        <>
                            <Text styles={{ root: classes.hourMinute }} style={{ width: 70, left: 'calc(50% - 70px)' }}>
                                <b className={classes.number}>{values.hours}</b> hour{values.hours > 2 ? 's' : ''}
                            </Text>
                            <Text styles={{ root: classes.hourMinute }} style={{ width: 100, right: 'calc(50% - 100px)' }}>
                                <b className={classes.number}>{values.minutes}</b> minute{values.minutes > 1 ? 's' : ''}
                            </Text>
                        </>
                    )}
                </Stack>
            )}
            <div className={classes.dragContainer}>
                <div className={classes.overlayContainer} style={{ transform: `scale(${isDragging ? '1.025, 1.2' : 1})` }}>
                    <div
                        className={classes.overlay}
                        style={{ width: `${sliderProcessWidth}%`, backgroundColor: isDragging ? theme.palette.themeDarkAlt : theme.palette.neutralLighter }}
                    />
                </div>
                <Text className={classes.dragText}>drag to register time</Text>
                <div {...bind()} className={classes.drag} />
            </div>
        </div>
    );
};

const getStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
    },
    dragContainer: {
        marginTop: 8,
        height: 26,
        width: '100%',
        position: 'relative',
    },
    drag: {
        position: 'absolute',
        inset: 0,
        zIndex: 5,
    },
    dragText: {
        position: 'absolute',
        inset: 0,
        fontSize: 11,
        fontWeight: 300,
        color: theme.semanticColors.bodySubtext,
        textAlign: 'center',
        lineHeight: '26px',
        zIndex: 2,
        opacity: 0.2,
    },
    overlayContainer: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: theme.semanticColors.bodyFrameBackground,
        borderRadius: 4,
        border: `1px solid ${theme.palette.neutralLighter}`,
        transition: 'transform 75ms',
        overflow: 'hidden',
    },
    overlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        backgroundColor: theme.palette.neutralLighter,
        transition: 'background-color 75ms',
    },
    textContainer: {
        position: 'relative',
        height: 36,
        width: '100%',
    },
    numberInputContainer: {
        boxShadow: `inset 0 0 0 1px ${theme.palette.themePrimary}, 0 0 0 1px ${theme.palette.themePrimary}`,
        borderRadius: 4,
    },
    numberInput: {
        width: '100%',
        height: 36,
        padding: `0 ${theme.spacing.s1}`,
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: '400',
        border: '0px solid rgba(0,0,0,0)',
        // boxShadow: `0 0 1px ${theme.palette.neutralLighter}`,
        borderRadius: 4,
        color: theme.semanticColors.bodyText,
        backgroundColor: 'rgba(0,0,0,0)',
        outline: 'none',
        // boxShadow: `inset 0 0 0 1px ${theme.palette.themePrimary}, 0 0 0 1px ${theme.palette.themePrimary}`,
        // "&:focus-visible, &:focused, &:active": {
        //     boxShadow: `inset 0 0 0 1px ${theme.palette.themePrimary}, 0 0 0 1px ${theme.palette.themePrimary}`,
        // },
    },
    number: {
        color: theme.semanticColors.bodyText,
        fontWeight: 400,
        width: 26,
        textAlign: 'right',
        paddingRight: 6,
        userSelect: 'none',
    },
    percent: {
        color: theme.semanticColors.bodyText,
        fontWeight: 400,
        flexGrow: 1,
        textAlign: 'right',
        paddingRight: 6,
        userSelect: 'none',
    },
    hourMinute: {
        color: theme.semanticColors.bodySubtext,
        fontWeight: 300,
        display: 'flex',
        alignItems: 'center',
        fontSize: 14,
        width: 100,
        textAlign: 'left',
        position: 'absolute',
        top: 0,
        bottom: 0,
        userSelect: 'none',
    },
}));
